import type { ClaimSetHistoryVisitedRecord } from '@/domains/claimSets/services/ClaimSetHistoryVisitsService/types/ClaimSetHistoryVisitedRecord';
import ClaimSetHistoryVisitsServiceEvents from '@/domains/claimSets/services/ClaimSetHistoryVisitsService/events';
import { LocalStorageVariables } from '@/enums/LocalStorageVariables';
import EventService from '@/services/EventService/EventService';

const storageKey = LocalStorageVariables.ClaimSetVisitsStorage;

export class ClaimSetHistoryVisitsService {
  public static save(claimSetNumber: string | number, claimSetId: number | string) {
    const storage: Record<string, ClaimSetHistoryVisitedRecord> = this.getStorage();

    if (!storage || String(claimSetNumber) in storage) return null;

    storage[String(claimSetNumber)] = {
      claimSetNumber: String(claimSetNumber),
      claimSetId: String(claimSetId),
      _deleted: false,
      _createdAt: new Date().getTime(),
    };
    this.setStorage(storage);
  }

  public static read(claimSetNumber: string): ClaimSetHistoryVisitedRecord | null {
    return this.getStorage()?.[String(claimSetNumber)] || null;
  }

  public static readAll(withoutDeleted?: boolean): ClaimSetHistoryVisitedRecord[] {
    const storage = this.getStorage();
    const list = Object.keys(storage).map((key) => storage[key]);
    const sortedList = this.sortByCreatedAt(list);
    return withoutDeleted ? sortedList.filter((item) => !item._deleted) : sortedList;
  }

  public static readMany(amount: number, withoutDeleted?: boolean) {
    const list = this.readAll(withoutDeleted);
    return list.slice(0, amount).filter(Boolean);
  }

  public static delete(claimSetNumber: number | string) {
    this.init();

    const storage = this.getStorage();

    if (storage && String(claimSetNumber) in storage) {
      storage[String(claimSetNumber)]._deleted = true;
      this.setStorage(storage);
    }
  }

  public static clean() {
    this.init();
    this.setStorage({});
  }

  protected static sortByCreatedAt(list: ClaimSetHistoryVisitedRecord[]) {
    return list.sort((a, b) => {
      const dateA = new Date(a._createdAt).getTime();
      const dateB = new Date(b._createdAt).getTime();
      return dateB - dateA;
    });
  }

  protected static setStorage(storage: Record<string, ClaimSetHistoryVisitedRecord>) {
    localStorage.setItem(storageKey, JSON.stringify(storage));
    EventService.dispatch(new ClaimSetHistoryVisitsServiceEvents.StorageUpdated({}));
  }

  protected static getStorage(): Record<string, ClaimSetHistoryVisitedRecord> {
    this.init();
    return JSON.parse(localStorage.getItem(storageKey) as string) || {};
  }

  protected static init() {
    if (!localStorage.getItem(storageKey)) {
      localStorage.setItem(storageKey, JSON.stringify({}));
    }
  }
}

export default ClaimSetHistoryVisitsService;
